import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Badge,
  Pagination,
} from "react-bootstrap";
import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";


import { fetchFinancialYearsThunk } from "../../../store/slices/financialYearSlice";
import {
  fetchSlrmDcbThunk,
  clearSlrmDcb,
} from "../../../store/slices/slrmDcbSlice";

import { printTableReport } from "./reportPrintUtils";

import "./ReportPage.css";

function fyToStartYear(fyStr) {
  const value = String(fyStr || "").trim();
  const match = value.match(/\d{4}/);

  return match ? match[0] : "";
}

function buildRowsWithBlanks(rows = []) {
  const output = [];

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    output.push(row);

    if (row.__blankAfterHouse) {
      output.push({
        __blank: true,
        __key: `b1-${row.home_id}-${index}`,
      });

      output.push({
        __blank: true,
        __key: `b2-${row.home_id}-${index}`,
      });
    }
  }

  return output;
}

const SlrmDcbReportPage = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const homeIdParam = String(
    searchParams.get("home_id") || "",
  ).trim();

  const home_id = homeIdParam
    ? Number(homeIdParam)
    : 0;

  const fyList = useSelector(
    (state) => state.financialYears?.list || [],
  );

  const fyLoading = useSelector(
    (state) => state.financialYears?.loading,
  );

  const {
    rows = [],
    loading = false,
    error = null,
    meta = {},
  } = useSelector(
    (state) => state.slrmDcb || {},
  );

  const [fyYear, setFyYear] = useState("");
  const [search, setSearch] = useState("");

  const [sortField, setSortField] =
    useState("home_id");

  const [sortDirection, setSortDirection] =
    useState("desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] =
    useState(20);

  const [exportLoading, setExportLoading] =
    useState(false);

  useEffect(() => {
    dispatch(fetchFinancialYearsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!fyList.length || fyYear) {
      return;
    }

    const current = fyList.find(
      (item) => Number(item?.current) === 1,
    );

    const startYear = fyToStartYear(
      current?.financial_year,
    );

    if (startYear) {
      setFyYear(startYear);
    }
  }, [fyList, fyYear]);

  const getRequestFilters = (
    requestPage,
    requestPageSize,
    exportValue = 0,
  ) => ({
    fy_year: fyYear,
    home_id: home_id || "",
    page: requestPage,
    pageSize: requestPageSize,

    ...(sortField
      ? {
          sort_field: sortField,
          sort_direction: sortDirection,
        }
      : {}),

    export: exportValue,
  });

  const handleLoad = () => {
    if (!fyYear || !pageSize) {
      return;
    }

    setPage(1);

    dispatch(
      fetchSlrmDcbThunk(
        getRequestFilters(
          1,
          pageSize,
          0,
        ),
      ),
    );
  };

  useEffect(() => {
    if (!fyYear || !pageSize) {
      return;
    }

    dispatch(
      fetchSlrmDcbThunk(
        getRequestFilters(
          page,
          pageSize,
          0,
        ),
      ),
    );
  }, [
    dispatch,
    fyYear,
    home_id,
    page,
    pageSize,
    sortField,
    sortDirection,
  ]);

  const handleClear = () => {
    setFyYear("");
    setSearch("");
    setSortField("home_id");
    setSortDirection("desc");
    setPage(1);
    setPageSize(20);

    dispatch(clearSlrmDcb());
    dispatch(fetchFinancialYearsThunk());
  };

  const handleSort = (field) => {
    setPage(1);

    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection(
          (currentDirection) =>
            currentDirection === "asc"
              ? "desc"
              : "asc",
        );

        return currentField;
      }

      setSortDirection("asc");
      return field;
    });
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp
        size={14}
        className="ms-1"
      />
    ) : (
      <ChevronDown
        size={14}
        className="ms-1"
      />
    );
  };

  const filteredRows = useMemo(() => {
    const query = search
      .trim()
      .toLowerCase();

    const baseRows = !query
      ? [...rows]
      : rows.filter((row) => {
          const searchableText = `
            ${row.family_head}
            ${row.qr_code}
            ${row.door_no}
            ${row.date}
            ${row.bill_no}
            ${row.rct_no}
            ${row.mode}
            ${row.opening_balance}
            ${row.bill_amount}
            ${row.rct_amount}
            ${row.discount_amount}
            ${row.closing_balance}
          `
            .toLowerCase()
            .replace(/\s+/g, " ");

          return searchableText.includes(query);
        });

    /*
     * Backend provides house_serial.
     * Do not recalculate serial numbers here.
     */
    const numberedRows = baseRows.map(
      (row) => ({
        ...row,
        __groupNo: row.house_serial,
      }),
    );

    const rowsWithBlankFlags =
      numberedRows.map((row, index) => {
        const next = numberedRows[index + 1];

        const isLastOfHouse =
          !next ||
          Number(next.home_id) !==
            Number(row.home_id);

        return {
          ...row,
          __blankAfterHouse:
            isLastOfHouse,
        };
      });

    return buildRowsWithBlanks(
      rowsWithBlankFlags,
    );
  }, [rows, search]);

  const buildExportRows = (responseRows) => {
    const exportRows = (
      responseRows || []
    ).map((row) => ({
      ...row,
      __groupNo: row.house_serial,
    }));

    return buildRowsWithBlanks(
      exportRows.map((row, index) => {
        const next = exportRows[index + 1];

        const isLastOfHouse =
          !next ||
          Number(next.home_id) !==
            Number(row.home_id);

        return {
          ...row,
          __blankAfterHouse:
            isLastOfHouse,
        };
      }),
    );
  };

  const exportExcel = async () => {
    if (!fyYear || exportLoading) {
      return;
    }

    try {
      setExportLoading(true);

      const response = await dispatch(
        fetchSlrmDcbThunk(
          getRequestFilters(
            1,
            999999,
            1,
          ),
        ),
      ).unwrap();

      const expanded = buildExportRows(
        response?.data || [],
      );

      const data = expanded.map((row) => {
        if (row.__blank) {
          return {
            "#": "",
            "Family Head": "",
            Meter: "",
            Door: "",
            Date: "",
            Opening: "",
            "Bill No": "",
            "Bill Amt": "",
            "Receipt No": "",
            Mode: "",
            "Receipt Amt": "",
            "Discount Amt": "",
            Closing: "",
          };
        }

        return {
          "#":
            row.s_no === 1
              ? row.house_serial
              : "",

          "Family Head":
            row.s_no === 1
              ? row.family_head || ""
              : "",

          Meter:
            row.s_no === 1
              ? row.qr_code || ""
              : "",

          Door:
            row.s_no === 1
              ? row.door_no || ""
              : "",

          Date: row.date ?? "",

          Opening:
            row.opening_balance ?? "",

          "Bill No":
            row.bill_no ?? "",

          "Bill Amt":
            row.bill_amount ?? "",

          "Receipt No":
            row.rct_no ?? "",

          Mode:
            row.mode ?? "",

          "Receipt Amt":
            row.rct_amount ?? "",

          "Discount Amt":
            row.discount_amount ?? "",

          Closing:
            row.closing_balance ?? "",
        };
      });

      const worksheet =
        XLSX.utils.json_to_sheet(data);

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "SLRM DCB",
      );

      const fileTag = home_id
        ? `home_${home_id}`
        : "all_records";

      XLSX.writeFile(
        workbook,
        `slrm_dcb_fy_${
          fyYear || "NA"
        }_${fileTag}.xlsx`,
      );
    } catch (exportError) {
      console.error(
        "Excel export failed:",
        exportError,
      );
    } finally {
      setExportLoading(false);
    }
  };

  const exportPDF = async () => {
    if (!fyYear || exportLoading) return;

    try {
      setExportLoading(true);
      const response = await dispatch(
        fetchSlrmDcbThunk(getRequestFilters(1, 999999, 1)),
      ).unwrap();
      const expanded = buildExportRows(response?.data || []);

      const data = expanded.map((row) => {
        if (row.__blank) {
          return {
            __blank: true,
            "#": "", "Family Head": "", Meter: "", Door: "", Date: "",
            Opening: "", "Bill No": "", "Bill Amt": "", "Receipt No": "",
            Mode: "", "Receipt Amt": "", "Discount Amt": "", Closing: "",
          };
        }
        return {
          "#": row.s_no === 1 ? row.house_serial : "",
          "Family Head": row.s_no === 1 ? row.family_head || "" : "",
          Meter: row.s_no === 1 ? row.qr_code || "" : "",
          Door: row.s_no === 1 ? row.door_no || "" : "",
          Date: row.date ?? "",
          Opening: row.opening_balance ?? "",
          "Bill No": row.bill_no ?? "",
          "Bill Amt": row.bill_amount ?? "",
          "Receipt No": row.rct_no ?? "",
          Mode: row.mode ?? "",
          "Receipt Amt": row.rct_amount ?? "",
          "Discount Amt": row.discount_amount ?? "",
          Closing: row.closing_balance ?? "",
        };
      });

      const fileTag = home_id ? `Home ID: ${home_id}` : "All Records";
      printTableReport({
        title: "SLRM DCB - Report",
fileName: "slrm_dcb_report.pdf",
        data,
        metaLines: [
          `FY: ${fyYear || "-"}`,
          `Date Range: ${response?.meta?.start_date || meta?.start_date || "-"} to ${response?.meta?.end_date || meta?.end_date || "-"}`,
          fileTag,
        ],
      });
    } catch (exportError) {
      console.error("PDF export failed:", exportError);
    } finally {
      setExportLoading(false);
    }
  };

  const totalPages = Math.max(
    1,
    Number(meta?.totalPages || 1),
  );

  const currentPage = Math.max(
    1,
    Number(meta?.page || page),
  );

  const totalRecords = Number(
    meta?.totalRecords || 0,
  );

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    const items = [];
    const maxButtons = 5;

    let start = Math.max(
      1,
      currentPage -
        Math.floor(maxButtons / 2),
    );

    let end = Math.min(
      totalPages,
      start + maxButtons - 1,
    );

    start = Math.max(
      1,
      end - maxButtons + 1,
    );

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={
          currentPage === 1 || loading
        }
        onClick={() =>
          setPage((previous) =>
            Math.max(1, previous - 1),
          )
        }
      />,
    );

    for (
      let pageNumber = start;
      pageNumber <= end;
      pageNumber += 1
    ) {
      items.push(
        <Pagination.Item
          key={pageNumber}
          active={
            pageNumber === currentPage
          }
          onClick={() =>
            setPage(pageNumber)
          }
          disabled={loading}
        >
          {pageNumber}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={
          currentPage === totalPages ||
          loading
        }
        onClick={() =>
          setPage((previous) =>
            Math.min(
              totalPages,
              previous + 1,
            ),
          )
        }
      />,
    );

    return (
      <Pagination className="mb-0">
        {items}
      </Pagination>
    );
  };

  const SortableHeader = ({
    field,
    children,
  }) => (
    <th>
      <button
        type="button"
        className={`report-sort-btn ${
          sortField === field
            ? "is-active"
            : ""
        }`}
        onClick={() =>
          handleSort(field)
        }
      >
        {children}
        {renderSortIcon(field)}
      </button>
    </th>
  );

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                SLRM Demand–Collection–Balance (DCB) Report{" "}
                {home_id ? (
                  <Badge
                    bg="info"
                    className="ms-2"
                  >
                    House ID: {home_id}
                  </Badge>
                ) : null}
              </div>

              <div className="report-subtitle">
                {home_id
                  ? "Single House SLRM Passbook (Home-wise Ledger)"
                  : "Financial Year wise SLRM DCB for all SLRM houses"}
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          <div className="report-filter-box">
            <Row className="g-3">
              <Col
                lg={home_id ? 6 : 4}
                md={6}
              >
                <Form.Label className="report-label">
                  Financial Year
                </Form.Label>

                <Form.Select
                  value={fyYear}
                  onChange={(event) => {
                    setFyYear(
                      event.target.value,
                    );

                    setPage(1);
                  }}
                  disabled={
                    fyLoading || loading
                  }
                >
                  <option value="">
                    Select FY
                  </option>

                  {fyList.map((item) => {
                    const startYear =
                      fyToStartYear(
                        item?.financial_year,
                      );

                    if (!startYear) {
                      return null;
                    }

                    return (
                      <option
                        key={String(item.id)}
                        value={startYear}
                      >
                        {item.financial_year}{" "}
                        {Number(
                          item.current,
                        ) === 1
                          ? "(Current)"
                          : ""}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>

              {!home_id ? (
                <Col lg={4} md={6}>
                  <Form.Label className="report-label">
                    Records Per Page
                  </Form.Label>

                  <Form.Control
                    type="number"
                    min={1}
                    value={pageSize || ""}
                    onChange={(event) => {
                      const value =
                        event.target.value;

                      setPage(1);

                      setPageSize(
                        value
                          ? Math.max(
                              1,
                              Number(value),
                            )
                          : "",
                      );
                    }}
                    placeholder="Eg: 20"
                    disabled={loading}
                  />

                  <div
                    className="text-muted mt-1"
                    style={{ fontSize: 12 }}
                  >
                    Enter how many house
                    records you want per page
                  </div>
                </Col>
              ) : (
                <Col lg={6} md={6}>
                  <Form.Label className="report-label">
                    Mode
                  </Form.Label>

                  <Form.Control
                    value="Single House (home_id)"
                    disabled
                  />

                  <div
                    className="text-muted mt-1"
                    style={{ fontSize: 12 }}
                  >
                    This page is opened from
                    House Profile → View SLRM DCB
                  </div>
                </Col>
              )}

              <Col
                lg={4}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={handleLoad}
                    disabled={
                      loading ||
                      !fyYear ||
                      !pageSize
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          size="sm"
                          className="me-2"
                        />
                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={handleClear}
                    disabled={loading}
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="report-table-toolbar">
            <div className="report-table-title">
              SLRM DCB{" "}
              <span className="report-count">
                (FY: {fyYear || "-"} •
                Houses in current result:{" "}
                {meta?.returned_houses ?? 0})
              </span>

              {meta?.start_date &&
              meta?.end_date ? (
                <div
                  className="text-muted"
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  Range: {meta.start_date} →
                  {meta.end_date}
                </div>
              ) : null}

              {!home_id ? (
                <div
                  className="text-muted"
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  Total Houses:{" "}
                  {totalRecords}
                </div>
              ) : null}
            </div>

            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>

                <Form.Control
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value,
                    )
                  }
                  placeholder="Search within current page rows..."
                />
              </InputGroup>

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={
                  !rows.length ||
                  loading ||
                  exportLoading
                }
              >
                {exportLoading ? (
                  <Spinner
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <Download
                    size={16}
                    className="me-2"
                  />
                )}

                {exportLoading
                  ? "Exporting..."
                  : "Excel"}
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={
                  !rows.length ||
                  loading ||
                  exportLoading
                }
              >
                {exportLoading ? (
                  <Spinner
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <Download
                    size={16}
                    className="me-2"
                  />
                )}

                {exportLoading
                  ? "Exporting..."
                  : "PDF"}
              </Button>
            </div>
          </div>

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table
                hover
                className="mb-0 report-table"
              >
                <thead>
                  <tr>
                    <SortableHeader field="home_id">
                      #
                    </SortableHeader>

                    <SortableHeader field="family_head">
                      Family Head
                    </SortableHeader>

                    <SortableHeader field="qr_code">
                      Meter
                    </SortableHeader>

                    <SortableHeader field="door_no">
                      Door
                    </SortableHeader>

                    <th>Date</th>
                    <th>Opening</th>
                    <th>Bill No</th>
                    <th>Bill Amt</th>
                    <th>Receipt No</th>
                    <th>Mode</th>
                    <th>Receipt Amt</th>
                    <th>Discount Amt</th>
                    <th>Closing</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={13}
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading...
                      </td>
                    </tr>
                  ) : filteredRows.length ? (
                    filteredRows.map(
                      (row, index) => {
                        if (row.__blank) {
                          return (
                            <tr
                              key={
                                row.__key ||
                                `blank-${index}`
                              }
                              className="dcb-blank-row"
                            >
                              <td colSpan={13}>
                                &nbsp;
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr
                            key={`${row.home_id}-${row.s_no}-${index}`}
                          >
                            <td>
                              {row.s_no === 1
                                ? row.house_serial
                                : ""}
                            </td>

                            <td>
                              {row.s_no === 1
                                ? row.family_head
                                : ""}
                            </td>

                            <td>
                              {row.s_no === 1
                                ? row.qr_code
                                : ""}
                            </td>

                            <td>
                              {row.s_no === 1
                                ? row.door_no
                                : ""}
                            </td>

                            <td>{row.date}</td>
                            <td>{row.opening_balance}</td>
                            <td>{row.bill_no}</td>
                            <td>{row.bill_amount}</td>
                            <td>{row.rct_no}</td>
                            <td>{row.mode}</td>
                            <td>{row.rct_amount}</td>
                            <td>{row.discount_amount}</td>
                            <td>{row.closing_balance}</td>
                          </tr>
                        );
                      },
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={13}
                        className="text-center py-4"
                      >
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>

                <Form.Select
                  value={pageSize}
                  onChange={(event) => {
                    setPage(1);

                    setPageSize(
                      Number(
                        event.target.value,
                      ),
                    );
                  }}
                  className="page-size-select"
                  disabled={loading}
                >
                  {[5, 10, 20, 50, 100].map(
                    (size) => (
                      <option
                        key={size}
                        value={size}
                      >
                        {size}
                      </option>
                    ),
                  )}
                </Form.Select>
              </div>

              <div className="report-pagination">
                {renderPagination()}
              </div>

              <div className="report-page-info">
                Page {currentPage} /{" "}
                {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SlrmDcbReportPage;
